import { on } from '@ember/modifier';
import { action } from '@ember/object';
import { guidFor } from '@ember/object/internals';
import { next } from '@ember/runloop';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';

import type { Service } from 'webapp-types';
import { Direction, PopupEvent } from 'webapp/enums/enums';

import didInsert from 'webapp/modifiers/did-insert';
import CommonUiIconComponent from 'webapp/components/common/ui-icon';
import CustomQuerySetupCommonDimensionSelectorPopupComponent from 'webapp/components/custom-query/setup/common/dimension-selector/popup';
import { NONE } from 'webapp/components/form/select-dimensions';
import type { ActionOption } from 'webapp/components/form/select-with-action';
import FormSelectWithActionComponent from 'webapp/components/form/select-with-action';

import type CustomMetricSetupFormBase from 'webapp/models-custom/custom-metric-setup-form/base';

interface Args {
	form: CustomMetricSetupFormBase;
	isInline?: boolean;
	theme?: string;
}

export default class CustomQuerySetupCommonDimensionSelectorComponent extends Component<Args> {
	@service('popup') declare popupService: Service.Popup;

	// common

	get availableDimensions() {
		const dimensions = Array.from(this.args.form.availableDimensions);
		dimensions.unshift({
			key: NONE,
			value: 'None',
		});
		return dimensions;
	}

	get selectedBreakdownBy() {
		return this.args.form.selectedPreviewDimension || NONE;
	}

	get isDisabled() {
		return this.args.form.isLoadingPreview;
	}

	@action
	selectPreviewDimension(dimension: string) {
		const normalizedDimension = !dimension || dimension === NONE ? '' : dimension;
		if (normalizedDimension !== this.args.form.selectedPreviewDimension) {
			this.args.form.selectedPreviewDimension = normalizedDimension;
			void this.args.form.queryPreview();
		}
	}

	// for inline

	@tracked isPopupOpen = false;

	rangeSelectorNode: HTMLElement | null = null;

	get selectedText() {
		return (
			this.availableDimensions.find((dimension) => dimension.key === this.selectedBreakdownBy)
				?.value || 'None'
		);
	}

	@action
	openDropdown() {
		if (this.isDisabled) {
			return;
		}

		const rangeSelectorNode = this.rangeSelectorNode;

		if (!rangeSelectorNode) {
			return;
		}

		const attachTo = rangeSelectorNode.querySelector<HTMLElement>('span.date')!;

		if (!attachTo) {
			return;
		}

		if (this.popupService.isOpen(CustomQuerySetupCommonDimensionSelectorPopupComponent)) {
			return;
		}

		const popup = this.popupService.createContext(
			CustomQuerySetupCommonDimensionSelectorPopupComponent,
			attachTo,
			{
				popupClass: 'optionspopup popup-type-qb-dimensions',
				directions: [Direction.DOWN, Direction.UP],
				theme: this.args.theme,
			},
		);

		popup.on(PopupEvent.OPENING, this, () => {
			this.isPopupOpen = true;
		});

		popup.open({
			selected: this.args.form.selectedPreviewDimension || NONE,
			options: this.availableDimensions,
			onSelect: this.selectPreviewDimension,
			onRefresh: () => {
				void this.args.form.triggerRefetchPreview().then(() => {
					this.openDropdown();
				});
			},
		});

		popup.on(PopupEvent.CLOSING, this, () => {
			this.isPopupOpen = false;
		});
	}

	@action
	setupRangeSelectorNode(node: HTMLElement) {
		this.rangeSelectorNode = node;
	}

	// for select dropdown

	uniqueId = 'select-' + guidFor(this);

	get options() {
		return this.availableDimensions.map((d) => {
			return {
				value: d.key,
				label: d.value,
			};
		});
	}

	get actionOptions(): ActionOption[] {
		return [
			{
				id: null,
				text: 'Refresh list',
				isSpecial: true,
				onClick: (selectElement: JQuery<HTMLElement>) => {
					void this.args.form.triggerRefetchPreview().then(() => {
						next(() => {
							selectElement.select2('open');
						});
					});
				},
				html: '<a role="button" class="button-refresh"><ui-icon name="Refresh" size="sm"></ui-icon><span>Refresh list</span></a>',
				position: 'bottom',
			},
		];
	}
	<template>
		{{#if @isInline}}
			{{! reusing the classes / styles from the range picker }}
			<div
				class="cdr-range-inline
					{{if this.isPopupOpen 'cdr-range-inline--open'}}
					{{if this.isDisabled 'disabled'}}"
			>
				<span class="range-label">Breakdown by</span>
				<a
					class="range-selector em"
					{{didInsert this.setupRangeSelectorNode}}
					{{on "click" this.openDropdown}}
				>
					<span class="date">{{this.selectedText}}</span>
					<CommonUiIconComponent @name="ChevronDown" @size="sm" />
				</a>
			</div>
		{{else}}
			<FormSelectWithActionComponent
				name={{this.uniqueId}}
				@selected={{this.selectedBreakdownBy}}
				@options={{this.options}}
				@actionOptions={{this.actionOptions}}
				@onChange={{this.selectPreviewDimension}}
				@isNumeric={{false}}
				@floatlabelText="Breakdown by"
				@isDisabled={{this.isDisabled}}
				@isRequired={{true}}
				@tooltipOnSelectionFromProperty=""
				@theme={{@theme}}
			/>
		{{/if}}
	</template>
}
